/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { HelpArticleCreateRequest } from '../models/HelpArticleCreateRequest';
import type { HelpArticleDetail } from '../models/HelpArticleDetail';
import type { HelpArticleFreshnessAssessment } from '../models/HelpArticleFreshnessAssessment';
import type { HelpArticleMarkReviewedRequest } from '../models/HelpArticleMarkReviewedRequest';
import type { HelpArticleMetadataSuggestion } from '../models/HelpArticleMetadataSuggestion';
import type { HelpArticlePublishRequest } from '../models/HelpArticlePublishRequest';
import type { HelpArticleRead } from '../models/HelpArticleRead';
import type { HelpArticleReorderRequest } from '../models/HelpArticleReorderRequest';
import type { HelpArticleScopeTypeEnum } from '../models/HelpArticleScopeTypeEnum';
import type { HelpArticleStatusEnum } from '../models/HelpArticleStatusEnum';
import type { HelpArticleSummary } from '../models/HelpArticleSummary';
import type { HelpArticleUpsertRequest } from '../models/HelpArticleUpsertRequest';
import type { HelpCategoryRead } from '../models/HelpCategoryRead';
import type { HelpCategoryReorderRequest } from '../models/HelpCategoryReorderRequest';
import type { HelpCategoryUpsertRequest } from '../models/HelpCategoryUpsertRequest';
import type { HelpFreshnessCheckRequest } from '../models/HelpFreshnessCheckRequest';
import type { HelpFreshnessCheckResult } from '../models/HelpFreshnessCheckResult';
import type { HelpHomeResponse } from '../models/HelpHomeResponse';
import type { HelpPromptDismissalRead } from '../models/HelpPromptDismissalRead';
import type { HelpPromptDismissalRequest } from '../models/HelpPromptDismissalRequest';
import type { HelpSearchResponse } from '../models/HelpSearchResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DocumentationService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get Public Documentation Home
     * @param contextRoute
     * @returns HelpHomeResponse Successful Response
     * @throws ApiError
     */
    public getPublicDocumentationHome(
        contextRoute?: (string | null),
    ): CancelablePromise<HelpHomeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/public/home',
            query: {
                'context_route': contextRoute,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Public Documentation Article
     * @param slug
     * @returns HelpArticleDetail Successful Response
     * @throws ApiError
     */
    public getPublicDocumentationArticle(
        slug: string,
    ): CancelablePromise<HelpArticleDetail> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/public/articles/{slug}',
            path: {
                'slug': slug,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Search Public Documentation
     * @param q
     * @param contextRoute
     * @param limit
     * @returns HelpSearchResponse Successful Response
     * @throws ApiError
     */
    public searchPublicDocumentation(
        q: string = '',
        contextRoute?: (string | null),
        limit: number = 20,
    ): CancelablePromise<HelpSearchResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/public/search',
            query: {
                'q': q,
                'context_route': contextRoute,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Documentation Home
     * @param contextRoute
     * @param clusterId
     * @param entityId
     * @returns HelpHomeResponse Successful Response
     * @throws ApiError
     */
    public getDocumentationHome(
        contextRoute?: (string | null),
        clusterId?: (string | null),
        entityId?: (string | null),
    ): CancelablePromise<HelpHomeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/home',
            query: {
                'context_route': contextRoute,
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Documentation Feature Articles
     * @param featureKey
     * @param clusterId
     * @param entityId
     * @returns HelpArticleSummary Successful Response
     * @throws ApiError
     */
    public getDocumentationFeatureArticles(
        featureKey: string,
        clusterId?: (string | null),
        entityId?: (string | null),
    ): CancelablePromise<Array<HelpArticleSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/feature-articles',
            query: {
                'feature_key': featureKey,
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Documentation Article
     * @param slug
     * @param clusterId
     * @param entityId
     * @returns HelpArticleDetail Successful Response
     * @throws ApiError
     */
    public getDocumentationArticle(
        slug: string,
        clusterId?: (string | null),
        entityId?: (string | null),
    ): CancelablePromise<HelpArticleDetail> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/articles/{slug}',
            path: {
                'slug': slug,
            },
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Download Admin Article Pdf
     * @param articleId
     * @returns any The article rendered as a PDF.
     * @throws ApiError
     */
    public downloadAdminArticlePdf(
        articleId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/admin/articles/{article_id}/pdf',
            path: {
                'article_id': articleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Search Documentation
     * @param q
     * @param contextRoute
     * @param clusterId
     * @param entityId
     * @param limit
     * @returns HelpSearchResponse Successful Response
     * @throws ApiError
     */
    public searchDocumentation(
        q: string = '',
        contextRoute?: (string | null),
        clusterId?: (string | null),
        entityId?: (string | null),
        limit: number = 20,
    ): CancelablePromise<HelpSearchResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/search',
            query: {
                'q': q,
                'context_route': contextRoute,
                'cluster_id': clusterId,
                'entity_id': entityId,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Dismiss Help Prompt
     * @param requestBody
     * @returns HelpPromptDismissalRead Successful Response
     * @throws ApiError
     */
    public dismissHelpPrompt(
        requestBody: HelpPromptDismissalRequest,
    ): CancelablePromise<HelpPromptDismissalRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/prompt-dismissals',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Active Help Prompt Dismissal
     * @param featureKey
     * @param contextRoute
     * @param clusterId
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getActiveHelpPromptDismissal(
        featureKey?: (string | null),
        contextRoute?: (string | null),
        clusterId?: (string | null),
        entityId?: (string | null),
    ): CancelablePromise<(HelpPromptDismissalRead | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/prompt-dismissals/active',
            query: {
                'feature_key': featureKey,
                'context_route': contextRoute,
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Admin Articles
     * @param includeArchived
     * @param statusFilter
     * @param search
     * @param scopeType
     * @param scopeId
     * @returns HelpArticleRead Successful Response
     * @throws ApiError
     */
    public listAdminArticles(
        includeArchived: boolean = false,
        statusFilter?: (HelpArticleStatusEnum | null),
        search?: (string | null),
        scopeType?: (HelpArticleScopeTypeEnum | null),
        scopeId?: (string | null),
    ): CancelablePromise<Array<HelpArticleRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/admin/articles',
            query: {
                'include_archived': includeArchived,
                'status_filter': statusFilter,
                'search': search,
                'scope_type': scopeType,
                'scope_id': scopeId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Admin Article
     * @param requestBody
     * @returns HelpArticleDetail Successful Response
     * @throws ApiError
     */
    public createAdminArticle(
        requestBody: HelpArticleCreateRequest,
    ): CancelablePromise<HelpArticleDetail> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reorder Admin Articles
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public reorderAdminArticles(
        requestBody: HelpArticleReorderRequest,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/reorder',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Admin Article
     * @param articleId
     * @param requestBody
     * @returns HelpArticleDetail Successful Response
     * @throws ApiError
     */
    public updateAdminArticle(
        articleId: string,
        requestBody: HelpArticleUpsertRequest,
    ): CancelablePromise<HelpArticleDetail> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/documentation/admin/articles/{article_id}',
            path: {
                'article_id': articleId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Admin Draft Article
     * @param articleId
     * @returns void
     * @throws ApiError
     */
    public deleteAdminDraftArticle(
        articleId: string,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/documentation/admin/articles/{article_id}',
            path: {
                'article_id': articleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Publish Admin Article
     * @param articleId
     * @param requestBody
     * @returns HelpArticleDetail Successful Response
     * @throws ApiError
     */
    public publishAdminArticle(
        articleId: string,
        requestBody: HelpArticlePublishRequest,
    ): CancelablePromise<HelpArticleDetail> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/{article_id}/publish',
            path: {
                'article_id': articleId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Unpublish Admin Article
     * @param articleId
     * @returns HelpArticleDetail Successful Response
     * @throws ApiError
     */
    public unpublishAdminArticle(
        articleId: string,
    ): CancelablePromise<HelpArticleDetail> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/{article_id}/unpublish',
            path: {
                'article_id': articleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Archive Admin Article
     * @param articleId
     * @returns HelpArticleRead Successful Response
     * @throws ApiError
     */
    public archiveAdminArticle(
        articleId: string,
    ): CancelablePromise<HelpArticleRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/{article_id}/archive',
            path: {
                'article_id': articleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Admin Categories
     * @param clusterId
     * @returns HelpCategoryRead Successful Response
     * @throws ApiError
     */
    public listAdminCategories(
        clusterId?: (string | null),
    ): CancelablePromise<Array<HelpCategoryRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/admin/categories',
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Admin Category
     * @param requestBody
     * @returns HelpCategoryRead Successful Response
     * @throws ApiError
     */
    public createAdminCategory(
        requestBody: HelpCategoryUpsertRequest,
    ): CancelablePromise<HelpCategoryRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/categories',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reorder Admin Categories
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public reorderAdminCategories(
        requestBody: HelpCategoryReorderRequest,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/categories/reorder',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Admin Category
     * @param categoryId
     * @param requestBody
     * @returns HelpCategoryRead Successful Response
     * @throws ApiError
     */
    public updateAdminCategory(
        categoryId: string,
        requestBody: HelpCategoryUpsertRequest,
    ): CancelablePromise<HelpCategoryRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/documentation/admin/categories/{category_id}',
            path: {
                'category_id': categoryId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Admin Category
     * @param categoryId
     * @returns void
     * @throws ApiError
     */
    public deleteAdminCategory(
        categoryId: string,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/documentation/admin/categories/{category_id}',
            path: {
                'category_id': categoryId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Mark Admin Article Reviewed
     * @param articleId
     * @param requestBody
     * @returns HelpArticleRead Successful Response
     * @throws ApiError
     */
    public markAdminArticleReviewed(
        articleId: string,
        requestBody: HelpArticleMarkReviewedRequest,
    ): CancelablePromise<HelpArticleRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/{article_id}/mark-reviewed',
            path: {
                'article_id': articleId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Suggest Admin Article Metadata
     * @param articleId
     * @returns HelpArticleMetadataSuggestion Successful Response
     * @throws ApiError
     */
    public suggestAdminArticleMetadata(
        articleId: string,
    ): CancelablePromise<HelpArticleMetadataSuggestion> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/{article_id}/suggest-metadata',
            path: {
                'article_id': articleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assess Admin Article Freshness
     * @param articleId
     * @returns HelpArticleFreshnessAssessment Successful Response
     * @throws ApiError
     */
    public assessAdminArticleFreshness(
        articleId: string,
    ): CancelablePromise<HelpArticleFreshnessAssessment> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/articles/{article_id}/assess-freshness',
            path: {
                'article_id': articleId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Admin Freshness
     * @returns HelpArticleRead Successful Response
     * @throws ApiError
     */
    public listAdminFreshness(): CancelablePromise<Array<HelpArticleRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/admin/freshness',
        });
    }

    /**
     * List Admin Freshness Assessments
     * @returns HelpArticleFreshnessAssessment Successful Response
     * @throws ApiError
     */
    public listAdminFreshnessAssessments(): CancelablePromise<Array<HelpArticleFreshnessAssessment>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/documentation/admin/freshness/assessments',
        });
    }

    /**
     * Check Admin Freshness
     * @param requestBody
     * @returns HelpFreshnessCheckResult Successful Response
     * @throws ApiError
     */
    public checkAdminFreshness(
        requestBody: HelpFreshnessCheckRequest,
    ): CancelablePromise<Array<HelpFreshnessCheckResult>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/documentation/admin/freshness/check',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
